import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle, SignOut } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { AmountInput } from "../components/AmountInput";
import { useAuth } from "../context/AuthContext";
import { HelpWidget } from "../components/HelpWidget";

const STEPS = [
  { key: "pending_info", label: "Vos informations" },
  { key: "pending_plan", label: "Votre offre" },
  { key: "pending_payment", label: "Paiement" },
  { key: "pending_setup", label: "Configuration" },
];
const OFFERS = [
  ["starter", "Starter", "199 999 GNF/mois", "1 utilisateur, ventes, stock, documents"],
  ["pro", "Pro", "349 999 GNF/mois", "Jusqu'à 3 utilisateurs, alertes anti-fraude, IA"],
  ["business", "Business", "999 999 GNF/mois", "Équipe complète, RH, permissions détaillées"],
];

function Progress({ step }) {
  const index = STEPS.findIndex((s) => s.key === step);
  return (
    <ol data-testid="onboarding-progress" className="flex items-center gap-1.5 mb-4">
      {STEPS.map((s, i) => (
        <li key={s.key} className="flex-1">
          <span className={`block h-1.5 rounded-full ${i <= index ? "bg-[#C9793A]" : "bg-[#E0DACE]"}`} />
          <span className={`text-[11px] mt-1 block ${i === index ? "font-semibold" : "text-gray-400"}`}>{s.label}</span>
        </li>
      ))}
    </ol>
  );
}

export default function Onboarding() {
  const navigate = useNavigate();
  const { user, refresh, logout } = useAuth();
  const [state, setState] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [masked, setMasked] = useState("");
  const [info, setInfo] = useState({ company_name: "", sector: "", phone: "" });
  const [code, setCode] = useState("");
  const [setup, setSetup] = useState({ currency: "GNF", stock_mode: "with_stock", starting_cash: "" });

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/onboarding/status");
      setState(data);
      setInfo((f) => ({ company_name: f.company_name || data.company_name || "",
                        sector: f.sector || data.sector || "", phone: f.phone || data.phone || "" }));
      if (data.step === "pending_payment") navigate(`/paiement?offre=${data.offer || "starter"}`, { replace: true });
      if (data.step === "active") { await refresh(); navigate("/accueil", { replace: true }); }
    } catch (e) { setError(apiError(e)); }
  }, [navigate, refresh]);

  useEffect(() => { load(); }, [load]);

  const call = async (fn) => {
    setBusy(true); setError("");
    try { await fn(); } catch (e) { setError(apiError(e)); } finally { setBusy(false); }
  };

  const sendCode = () => call(async () => {
    const { data } = await api.post("/onboarding/info", info);
    setMasked(data.email_masked || user?.email || ""); setCodeSent(true);
  });

  const verifyCode = () => call(async () => {
    await api.post("/onboarding/info/verify", { code });
    setCodeSent(false); setCode(""); await load();
  });

  const choosePlan = (offer) => call(async () => {
    await api.post("/onboarding/plan", { offer });
    navigate(`/paiement?offre=${offer}`, { replace: true });
  });

  const finishSetup = () => call(async () => {
    await api.post("/onboarding/setup", {
      currency: setup.currency, stock_mode: setup.stock_mode,
      starting_cash: parseFloat(setup.starting_cash) || 0 });
    await refresh();
    navigate("/accueil", { replace: true });
  });

  if (!state) return <div className="min-h-screen flex items-center justify-center"><p className="font-display font-bold text-2xl lowercase animate-pulse">kavizio</p></div>;

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-8 pb-28 sm:pb-8" data-testid="onboarding-page">
      <div className="w-full max-w-md fade-in space-y-4">
        <p className="font-display font-bold text-4xl lowercase tracking-tight text-center">kavizio</p>
        <div className="kcard p-6 space-y-4">
          <Progress step={state.step} />
          {error && <p data-testid="onboarding-error" className="text-sm text-[#B4432F] bg-[#F6E3DF] rounded-xl px-3 py-2">{error}</p>}

          {state.step === "pending_info" && !codeSent && (
            <>
              <h1 className="font-display font-bold text-xl">Vos informations</h1>
              <p className="text-sm text-gray-600">
                Votre identité est confirmée ({state.email}). Complétez votre inscription comme pour un compte classique.
              </p>
              <input data-testid="onboarding-company-input" className="kinput" placeholder="Nom de l'entreprise"
                value={info.company_name} onChange={(e) => setInfo({ ...info, company_name: e.target.value })} />
              <input data-testid="onboarding-sector-input" className="kinput" placeholder="Secteur d'activité (optionnel)"
                value={info.sector} onChange={(e) => setInfo({ ...info, sector: e.target.value })} />
              <input data-testid="onboarding-phone-input" className="kinput" placeholder="Numéro de téléphone"
                value={info.phone} onChange={(e) => setInfo({ ...info, phone: e.target.value })} />
              <button data-testid="onboarding-info-btn" className="btn-amber w-full py-3" disabled={busy || !info.company_name || info.phone.length < 6}
                onClick={sendCode}>{busy ? "Envoi..." : "Recevoir le code de vérification"}</button>
            </>
          )}

          {state.step === "pending_info" && codeSent && (
            <>
              <h1 className="font-display font-bold text-xl">Vérification</h1>
              <p data-testid="onboarding-code-banner" className="text-sm bg-[#F6F4EF] text-gray-600 rounded-xl px-3 py-2">
                Un code vient d'être envoyé à <span className="font-semibold">{masked}</span>. Il est valable 15 minutes.
              </p>
              <input data-testid="onboarding-code-input" className="kinput font-mono-doc" placeholder="Code à 6 chiffres"
                value={code} onChange={(e) => setCode(e.target.value)} />
              <button data-testid="onboarding-verify-btn" className="btn-amber w-full py-3" disabled={busy || !code}
                onClick={verifyCode}>{busy ? "Vérification..." : "Valider"}</button>
              <button data-testid="onboarding-code-back-btn" className="btn-ghost w-full py-2.5 text-sm"
                onClick={() => setCodeSent(false)}>Modifier mes informations</button>
            </>
          )}

          {state.step === "pending_plan" && (
            <>
              <h1 className="font-display font-bold text-xl">Choisissez votre offre</h1>
              <div className="space-y-2">
                {OFFERS.map(([key, label, price, desc]) => (
                  <button key={key} type="button" data-testid={`onboarding-offer-${key}`} disabled={busy}
                    onClick={() => choosePlan(key)}
                    className="w-full text-left p-3.5 rounded-2xl border border-[#E0DACE] bg-white hover:border-[#C9793A] transition-colors">
                    <span className="text-sm font-semibold block">{label} — {price}</span>
                    <span className="text-xs text-gray-500">{desc}</span>
                  </button>
                ))}
              </div>
              <p className="text-xs text-gray-500">Vous réglerez ensuite votre abonnement. Aucun accès n'est ouvert avant la confirmation du paiement.</p>
            </>
          )}

          {state.step === "pending_setup" && (
            <>
              <h1 className="font-display font-bold text-xl">Configuration de départ</h1>
              <p className="text-sm text-[#2E7D5B] flex items-center gap-1.5">
                <CheckCircle size={16} weight="fill" /> Paiement confirmé. Dernière étape.
              </p>
              <div>
                <p className="text-sm font-semibold mb-2">Devise de votre entreprise</p>
                <div className="grid grid-cols-2 gap-2">
                  {[["GNF", "Franc Guinéen"], ["XOF", "Franc CFA"], ["EUR", "Euro"], ["USD", "Dollar US"]].map(([c, l]) => (
                    <button type="button" key={c} data-testid={`onboarding-currency-${c}`} onClick={() => setSetup({ ...setup, currency: c })}
                      className={`text-left p-3 rounded-2xl border transition-colors ${setup.currency === c ? "border-[#C9793A] bg-[#C9793A]/5" : "border-[#E0DACE] bg-white"}`}>
                      <span className="text-sm font-semibold block">{c}</span>
                      <span className="text-xs text-gray-500">{l}</span>
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-sm font-semibold mb-2">Gestion du stock</p>
                <div className="grid grid-cols-2 gap-2">
                  {[["with_stock", "Avec stock", "Produit par produit"], ["no_stock", "Sans stock", "Montant global"]].map(([v, t, d]) => (
                    <button type="button" key={v} data-testid={`onboarding-stock-${v}`} onClick={() => setSetup({ ...setup, stock_mode: v })}
                      className={`text-left p-3 rounded-2xl border transition-colors ${setup.stock_mode === v ? "border-[#C9793A] bg-[#C9793A]/5" : "border-[#E0DACE] bg-white"}`}>
                      <span className="text-sm font-semibold block">{t}</span>
                      <span className="text-xs text-gray-500">{d}</span>
                    </button>
                  ))}
                </div>
              </div>
              <AmountInput data-testid="onboarding-cash-input" onOverflow={(m) => toast.error(m)} placeholder="Trésorerie de départ (optionnel)"
                value={setup.starting_cash} onChange={(v) => setSetup({ ...setup, starting_cash: v })} />
              <button data-testid="onboarding-setup-btn" className="btn-amber w-full py-3" disabled={busy}
                onClick={finishSetup}>{busy ? "Enregistrement..." : "Ouvrir mon espace Kavizio"}</button>
            </>
          )}

          <div className="pt-2 border-t border-[#EFE9DE] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1.5">
            <button data-testid="onboarding-logout-btn" onClick={logout}
              className="text-xs text-gray-500 hover:text-[#16233D] flex items-center gap-1.5 self-start"><SignOut size={14} /> Se déconnecter</button>
            <span className="text-xs text-gray-400">Vous reprendrez où vous vous êtes arrêté.</span>
          </div>
        </div>
      </div>
      <HelpWidget />
    </div>
  );
}
